import React, { useState, useMemo, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  TextField,
  Autocomplete,
  Chip,
} from '@mui/material'
import {
  Description,
  Upload,
  Download,
  Delete,
  Visibility,
  Article,
  ReceiptLong,
  Payment,
  CameraAlt,
  AccountBalance,
  AccountBalanceWallet,
  Notes as NotesIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { Vehicle } from '@otw/models'
import { useDocumentsForInventoryObject, Document } from '@/hooks/inventory/useDocuments'
import { useInventory } from '@/hooks/inventory/useInventory'
import states from 'states-us'

interface ManageDocumentsDialogProps {
  collectionName: 'master_inventory' | 'shield_inventory' | 'imported_vehicles'
  open: boolean
  onClose: () => void
  vehicle: Vehicle
  /** When set (Inventory v3), open on this tab. Omitted by v2 so the last tab is left alone. */
  initialTab?: number
  onTabChange?: (tab: number) => void
}

const getVehicleOptionLabel = (option: Vehicle): string =>
  `${option.stock_number || 'N/A'} - ${option.year} ${option.make} ${option.model} (${option.vin || 'VIN N/A'})`

const ManageDocumentsDialog: React.FC<ManageDocumentsDialogProps> = ({
  collectionName,
  open,
  onClose,
  vehicle: row,
  initialTab,
  onTabChange,
}) => {
  const [activeTab, setActiveTab] = useState(0)
  const [documentNotes, setDocumentNotes] = useState(row?.document_notes ?? '')
  const [titleCopyState, setTitleCopyState] = useState<string | null>(row?.title_state ?? null)
  const [pendingPaymentFiles, setPendingPaymentFiles] = useState<File[]>([])
  const [additionalVehicles, setAdditionalVehicles] = useState<Vehicle[]>([])
  const { showSuccess, showError, showWarning } = useSnackbar()
  const isMobile = useIsMobile()

  const { updateDocument, vehicles: inventoryVehicles = [] } = useInventory(collectionName, undefined, {
    dontShowUpdates: true,
  })
  const {
    documents,
    isLoading,
    uploadDocumentAsync,
    uploadDocumentToVehiclesAsync,
    deleteDocument,
    downloadDocument,
    viewDocument,
    isUploading,
    isDeleting,
  } = useDocumentsForInventoryObject(collectionName as 'master_inventory' | 'shield_inventory', row.id, {
    enabled: true, // Match the photos hook exactly
  })

  useEffect(() => {
    if (open && row) {
      setDocumentNotes(row.document_notes ?? '')
      setTitleCopyState(row.title_state ?? null)
    }
  }, [open, row?.id, row?.document_notes, row?.title_state])

  useEffect(() => {
    if (open && initialTab !== undefined) {
      setActiveTab(initialTab)
    }
  }, [open, initialTab])

  useEffect(() => {
    if (!open) {
      setPendingPaymentFiles([])
      setAdditionalVehicles([])
    }
  }, [open])

  const documentTypes = [
    { value: 'title_copy', label: 'Title Copies', icon: <Article /> },
    { value: 'purchase_order', label: 'Purchase Orders', icon: <ReceiptLong /> },
    { value: 'payment_copy', label: 'Payment Copies', icon: <Payment /> },
    { value: 'invoice', label: 'Invoices', icon: <Description /> },
  ]
  
  const isInvoiceTab = activeTab === 3 && documentTypes[activeTab]?.value === 'invoice'
  const isPaymentCopyTab = activeTab === 2 && documentTypes[activeTab]?.value === 'payment_copy'

  // Calculate equity for payment copies divider
  const sellerAskingPrice = row?.seller_asking_price ?? 0
  const lienPayoffAmount = row?.lien_payoff_amount ?? 0
  const equity = sellerAskingPrice - lienPayoffAmount
  const hasLien = row?.has_lien === true
  const shouldShowPaymentDivider = isPaymentCopyTab && hasLien && equity > 0

  const additionalVehicleOptions = useMemo(
    () =>
      (inventoryVehicles || []).filter(
        vehicle =>
          vehicle.id !== row.id &&
          vehicle.status !== 'Sold' &&
          vehicle.status !== 'Sold elsewhere',
      ),
    [inventoryVehicles, row.id],
  )

  const selectTab = (index: number) => {
    setActiveTab(index)
    onTabChange?.(index)
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    selectTab(newValue)
  }

  const handleSaveNotes = () => {
    updateDocument(
      { id: row.id, data: { document_notes: documentNotes || null } },
      {
        onSuccess: () => showSuccess('Document notes saved.'),
        onError: (err) => showError(`Failed to save notes: ${err instanceof Error ? err.message : 'Unknown error'}`),
      },
    )
  }

  const clearPendingPaymentUpload = () => {
    setPendingPaymentFiles([])
    setAdditionalVehicles([])
  }

  const handleConfirmPaymentUpload = async () => {
    if (!pendingPaymentFiles.length) return

    const targetVehicles = [row, ...additionalVehicles]
    const vehicleIds = targetVehicles.map(vehicle => vehicle.id)
    const vehiclesMeta = targetVehicles.map(vehicle => ({
      id: vehicle.id,
      stockNumber: vehicle.stock_number,
      vin: vehicle.vin,
    }))

    try {
      for (const file of pendingPaymentFiles) {
        await uploadDocumentToVehiclesAsync({
          file,
          documentType: 'payment_copy',
          vehicleIds,
          vehiclesMeta,
        })
      }
    } catch {
      // Error toast is handled by the upload mutation
    } finally {
      clearPendingPaymentUpload()
    }
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files?.length) return

    // Invoices cannot be uploaded - they're generated from the system
    if (isInvoiceTab) {
      showWarning('Invoices cannot be uploaded. They are automatically saved when generated.')
      event.target.value = ''
      return
    }

    const documentType = documentTypes[activeTab].value as 'title_copy' | 'purchase_order' | 'payment_copy'
    const fileList = Array.from(files)
    event.target.value = ''

    if (documentType === 'payment_copy') {
      setPendingPaymentFiles(fileList)
      setAdditionalVehicles([])
      return
    }

    if (documentType === 'title_copy' && !titleCopyState) {
      showWarning('Select the title state before uploading a title copy.')
      return
    }

    try {
      for (const file of fileList) {
        await uploadDocumentAsync({
          file,
          documentType,
          ...(documentType === 'title_copy' ? { titleState: titleCopyState } : {}),
        })
      }
    } catch {
      // Error toast is handled by the upload mutation
    }
  }

  const handleDeleteDocument = async (document: Document) => {
    // Invoices cannot be deleted through this interface
    if (document.type === 'invoice') {
      showWarning('Invoices cannot be deleted from this dialog.')
      return
    }
    deleteDocument({ documentUrl: document.url, documentType: document.type as 'title_copy' | 'purchase_order' | 'payment_copy' })
  }

  const handleViewDocument = (document: Document) => {
    viewDocument(document)
  }

  const handleDownloadDocument = async (document: Document) => {
    downloadDocument(document)
  }

  const filteredDocuments = documents.filter(
    doc => doc.type === documentTypes[activeTab].value
  )

  // For title_copy tab, separate actual title photos from title copies
  const isTitleCopyTab = activeTab === 0 && documentTypes[activeTab].value === 'title_copy'
  
  // Actual title photos are those uploaded from CameraCaptureDialog (named title_front_* or title_back_*)
  const actualTitlePhotos = isTitleCopyTab
    ? filteredDocuments.filter(doc => {
        const fileName = doc.name.toLowerCase()
        return fileName.includes('title_front_') || fileName.includes('title_back_')
      })
    : []
  
  // Title copies are all other title_copy documents
  const titleCopies = isTitleCopyTab
    ? filteredDocuments.filter(doc => {
        const fileName = doc.name.toLowerCase()
        return !fileName.includes('title_front_') && !fileName.includes('title_back_')
      })
    : filteredDocuments

  // For payment_copy tab, separate equity from payoff documents when lien exists and equity is positive
  const equityPaymentCopies = shouldShowPaymentDivider
    ? filteredDocuments.filter(doc => {
        const fileName = doc.name.toLowerCase()
        return fileName.includes('equity')
      })
    : []
  
  const payoffPaymentCopies = shouldShowPaymentDivider
    ? filteredDocuments.filter(doc => {
        const fileName = doc.name.toLowerCase()
        // Include documents with "payoff" in name or documents that don't have "equity" in name
        return fileName.includes('payoff') || !fileName.includes('equity')
      })
    : filteredDocuments

  const paymentAttachDialog = (
    <Dialog
      open={pendingPaymentFiles.length > 0}
      onClose={isUploading ? undefined : clearPendingPaymentUpload}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {pendingPaymentFiles.length > 1 ? 'Upload Payment Copies' : 'Upload Payment Copy'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {pendingPaymentFiles.length === 1 ? (
            <>
              File: <strong>{pendingPaymentFiles[0]?.name}</strong>
            </>
          ) : (
            <>
              <strong>{pendingPaymentFiles.length} files</strong> selected:
              <Box component="ul" sx={{ m: 0, mt: 1, pl: 2 }}>
                {pendingPaymentFiles.map(file => (
                  <li key={file.name + file.size}>
                    <Typography variant="body2" component="span">
                      {file.name}
                    </Typography>
                  </li>
                ))}
              </Box>
            </>
          )}
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Use this when one wire covers multiple vehicles. The file{pendingPaymentFiles.length > 1 ? 's' : ''} will
          always be attached to {row?.stock_number || row?.id}. Optionally select additional vehicles below.
        </Alert>
        <Autocomplete
          multiple
          options={additionalVehicleOptions}
          value={additionalVehicles}
          onChange={(_event, value) => setAdditionalVehicles(value)}
          getOptionLabel={getVehicleOptionLabel}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={(options, params) => {
            const searchTerm = params.inputValue.toLowerCase()
            if (!searchTerm) return options
            return options.filter(vehicle =>
              vehicle.stock_number?.toLowerCase().includes(searchTerm) ||
              vehicle.vin?.toLowerCase().includes(searchTerm) ||
              vehicle.make?.toLowerCase().includes(searchTerm) ||
              vehicle.model?.toLowerCase().includes(searchTerm) ||
              vehicle.year?.toString().includes(searchTerm),
            )
          }}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id}>
              <Box>
                <Typography variant="body2">
                  {option.year} {option.make} {option.model}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Stock #: {option.stock_number || 'N/A'} | VIN: {option.vin || 'N/A'}
                </Typography>
              </Box>
            </Box>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                size="small"
                label={option.stock_number || option.vin || option.id}
              />
            ))
          }
          renderInput={params => (
            <TextField
              {...params}
              label="Also attach to other vehicles"
              placeholder="Search stock # or VIN"
              helperText="Optional — leave empty to upload only to this vehicle"
            />
          )}
          disabled={isUploading}
          sx={{ mt: 1 }}
        />
        {additionalVehicles.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Will upload to {additionalVehicles.length + 1} vehicles total.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={clearPendingPaymentUpload} disabled={isUploading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirmPaymentUpload}
          variant="contained"
          disabled={isUploading || pendingPaymentFiles.length === 0}
          startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <Upload />}
        >
          {isUploading
            ? 'Uploading...'
            : additionalVehicles.length > 0
              ? `Upload to ${additionalVehicles.length + 1} Vehicles`
              : 'Upload to This Vehicle'}
        </Button>
      </DialogActions>
    </Dialog>
  )

  return (
    <>
    {paymentAttachDialog}
    <Dialog 
      open={open} 
      onClose={isUploading ? undefined : onClose} 
      maxWidth="md" 
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Description />
            <Typography variant="h6">
              Documents - {row?.stock_number || row?.id} {row?.vin ? ` - ${row?.vin}` : ''}
            </Typography>
          </Box>
          <IconButton
            aria-label="Close dialog"
            onClick={onClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading vehicle documents...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Optional document notes */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <NotesIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="medium">
                  Document notes
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  (optional)
                </Typography>
              </Box>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                placeholder="Add notes about this vehicle's documents..."
                value={documentNotes}
                onChange={(e) => setDocumentNotes(e.target.value)}
                variant="outlined"
                size="small"
                sx={{ mb: 1 }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleSaveNotes}
                disabled={documentNotes === (row?.document_notes ?? '')}
              >
                Save notes
              </Button>
            </Box>

            <Box sx={{ ...(isMobile ? { mb: 2 } : { borderBottom: 1, borderColor: 'divider', mb: 2 }) }}>
              {isMobile ? (
                <FormControl fullWidth size="small">
                  <InputLabel id="document-type-select-label">Document type</InputLabel>
                  <Select
                    labelId="document-type-select-label"
                    label="Document type"
                    value={activeTab}
                    onChange={e => selectTab(Number(e.target.value))}
                    renderValue={(value) => {
                      const type = documentTypes[value as number]
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {type.icon}
                          {type.label}
                        </Box>
                      )
                    }}
                  >
                    {documentTypes.map((type, index) => (
                      <MenuItem key={type.value} value={index}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {type.icon}
                          {type.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Tabs value={activeTab} onChange={handleTabChange}>
                  {documentTypes.map((type, index) => (
                    <Tab
                      key={type.value}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {type.icon}
                          {type.label}
                        </Box>
                      }
                    />
                  ))}
                </Tabs>
              )}
            </Box>

            {/* Upload button - only show for title copies, purchase orders, and payment copies, not for invoices or actual title photos */}
            {!isTitleCopyTab && !isInvoiceTab && !shouldShowPaymentDivider && (
              <Box sx={{ mb: 2 }}>
                <input
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  style={{ display: 'none' }}
                  id="document-upload"
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  disabled={isUploading}
                />
                <label htmlFor="document-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<Upload />}
                    disabled={isUploading}
                    sx={{ mb: 2 }}
                  >
                    {isUploading ? 'Uploading...' : `Upload ${documentTypes[activeTab].label}`}
                  </Button>
                </label>
              </Box>
            )}
            
            {/* Info message for invoices tab */}
            {isInvoiceTab && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Invoices are automatically saved when generated from the inventory actions panel or lead management.
              </Alert>
            )}

            {isTitleCopyTab ? (
              <>
                {/* Actual Title Photos Section */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CameraAlt color="primary" />
                    <Typography variant="h6">In House Title Photos</Typography>
                    <Typography variant="caption" color="text.secondary">
                      (Uploaded from Camera Capture Dialog)
                    </Typography>
                  </Box>
                  {actualTitlePhotos.length === 0 ? (
                    <Alert severity="info">
                      No actual title photos uploaded yet. Use the Camera Capture Dialog to upload front and back photos.
                    </Alert>
                  ) : (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {actualTitlePhotos.length} photo{actualTitlePhotos.length !== 1 ? 's' : ''} uploaded
                      </Typography>
                      {actualTitlePhotos.map((document) => (
                        <Box
                          key={document.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            mb: 1,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <CameraAlt color="primary" />
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {document.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(document.uploadedAt).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="View document">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDocument(document)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download">
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadDocument(document)}
                              >
                                <Download />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete document">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteDocument(document)}
                                disabled={isDeleting}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Divider */}
                <Box sx={{ borderTop: 1, borderColor: 'divider', my: 3 }} />

                {/* Title Copies Section */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Article color="primary" />
                    <Typography variant="h6">Title Copies</Typography>
                    <Typography variant="caption" color="text.secondary">
                      (Uploaded from this dialog)
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 420 }}>
                    <Autocomplete
                      options={states}
                      getOptionLabel={(option) => `${option.name} (${option.abbreviation})`}
                      isOptionEqualToValue={(option, value) => option.abbreviation === value.abbreviation}
                      value={
                        titleCopyState
                          ? states.find((state) => state.abbreviation === titleCopyState) || null
                          : null
                      }
                      onChange={(_, newValue) => {
                        setTitleCopyState(newValue ? newValue.abbreviation : null)
                      }}
                      disabled={isUploading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Title state"
                          required
                          helperText="Required — where the title is from"
                        />
                      )}
                    />
                    <input
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      style={{ display: 'none' }}
                      id="title-copy-upload"
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      disabled={isUploading || !titleCopyState}
                    />
                    <label htmlFor="title-copy-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<Upload />}
                        disabled={isUploading || !titleCopyState}
                      >
                        {isUploading ? 'Uploading...' : 'Upload Title Copies'}
                      </Button>
                    </label>
                  </Box>

                  {titleCopies.length === 0 ? (
                    <Alert severity="info">
                      No title copies uploaded yet.
                    </Alert>
                  ) : (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {titleCopies.length} title cop{titleCopies.length !== 1 ? 'ies' : 'y'} uploaded
                      </Typography>
                      {titleCopies.map((document) => (
                        <Box
                          key={document.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            mb: 1,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Description color="primary" />
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {document.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(document.uploadedAt).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="View document">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDocument(document)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download">
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadDocument(document)}
                              >
                                <Download />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete document">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteDocument(document)}
                                disabled={isDeleting}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </>
            ) : isInvoiceTab ? (
              <>
                {/* Invoices tab */}
                {filteredDocuments.length === 0 ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No invoices generated yet. Generate invoices from the inventory actions panel.
                  </Alert>
                ) : (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {filteredDocuments.length} invoice{filteredDocuments.length !== 1 ? 's' : ''} generated
                    </Typography>
                    {filteredDocuments.map((document) => (
                      <Box
                        key={document.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 2,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Description color="primary" />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {document.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {document.clientName && `Client: ${document.clientName} • `}
                              {document.templateName && `${document.templateName} • `}
                              {new Date(document.uploadedAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View invoice in PandaDoc">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDocument(document)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            ) : shouldShowPaymentDivider ? (
              <>
                {/* Info message about filename requirement */}
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Important:</strong> For documents to be categorized correctly, the filename must include either "equity" or "payoff" (case-insensitive). 
                    Documents with "equity" in the filename will appear in the Equity section, and documents with "payoff" in the filename will appear in the Payoff section.
                  </Typography>
                </Alert>

                {/* Equity Payment Copies Section */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AccountBalance color="primary" />
                    <Typography variant="h6">Equity</Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <input
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      style={{ display: 'none' }}
                      id="equity-payment-upload"
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      disabled={isUploading}
                    />
                    <label htmlFor="equity-payment-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<Upload />}
                        disabled={isUploading}
                        sx={{ mb: 2 }}
                      >
                        {isUploading ? 'Uploading...' : 'Upload Equity Payment Copies'}
                      </Button>
                    </label>
                  </Box>

                  {equityPaymentCopies.length === 0 ? (
                    <Alert severity="info">
                      No equity payment copies uploaded yet.
                    </Alert>
                  ) : (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {equityPaymentCopies.length} equity payment cop{equityPaymentCopies.length !== 1 ? 'ies' : 'y'} uploaded
                      </Typography>
                      {equityPaymentCopies.map((document) => (
                        <Box
                          key={document.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            mb: 1,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Payment color="primary" />
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {document.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(document.uploadedAt).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="View document">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDocument(document)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download">
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadDocument(document)}
                              >
                                <Download />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete document">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteDocument(document)}
                                disabled={isDeleting}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Divider */}
                <Box sx={{ borderTop: 1, borderColor: 'divider', my: 3 }} />

                {/* Payoff Payment Copies Section */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AccountBalanceWallet color="primary" />
                    <Typography variant="h6">Payoff</Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <input
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      style={{ display: 'none' }}
                      id="payoff-payment-upload"
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      disabled={isUploading}
                    />
                    <label htmlFor="payoff-payment-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<Upload />}
                        disabled={isUploading}
                        sx={{ mb: 2 }}
                      >
                        {isUploading ? 'Uploading...' : 'Upload Payoff Payment Copies'}
                      </Button>
                    </label>
                  </Box>

                  {payoffPaymentCopies.length === 0 ? (
                    <Alert severity="info">
                      No payoff payment copies uploaded yet.
                    </Alert>
                  ) : (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {payoffPaymentCopies.length} payoff payment cop{payoffPaymentCopies.length !== 1 ? 'ies' : 'y'} uploaded
                      </Typography>
                      {payoffPaymentCopies.map((document) => (
                        <Box
                          key={document.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            mb: 1,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Payment color="primary" />
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {document.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {new Date(document.uploadedAt).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="View document">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDocument(document)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download">
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadDocument(document)}
                              >
                                <Download />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete document">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteDocument(document)}
                                disabled={isDeleting}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </>
            ) : (
              <>
                {/* Other tabs (Purchase Orders, Payment Copies) */}
                {filteredDocuments.length === 0 ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    No {documentTypes[activeTab].label.toLowerCase()} uploaded yet.
                  </Alert>
                ) : (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {filteredDocuments.length} {documentTypes[activeTab].label} uploaded
                    </Typography>
                    {filteredDocuments.map((document) => (
                      <Box
                        key={document.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 2,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Description color="primary" />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {document.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(document.uploadedAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View document">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDocument(document)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadDocument(document)}
                            >
                              <Download />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete document">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteDocument(document)}
                              disabled={isDeleting}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isUploading}>Close</Button>
      </DialogActions>
    </Dialog>
    </>
  )
}

export default ManageDocumentsDialog